import React, { useCallback, useEffect } from "react";
import PathPickFilterPanel from "@/features/pathpick/components/ui/PathPickFilterPanel";
import PathPickRangeFilterSection, {
  usePathPickRangeFilterChips,
} from "@/features/pathpick/components/ui/PathPickRangeFilterSection";
import usePathPickProductFilter, {
  PathPickProductFilter,
  PathPickProductFilterKey,
} from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import usePathPickProductFilterGroups, {
  getPathPickProductFilterValues,
  togglePathPickProductFilterValue,
} from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilterGroups";
import usePathPickFilterDraft from "@/features/pathpick/hooks/usePathPickFilterDraft";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import {
  getInvalidPathPickRangeFields,
  PathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// 상품 찾기 상세 필터 — 브랜드 / 카테고리 / 상품 유형 + 마진·거래수 범위.
// 패널 안 변경은 드래프트로 들고 있다가 `필터 적용`에서 한 번에 반영한다.
// 상품 유형은 상단 버튼과 같은 값(collection__in)이라, 버튼으로 바뀌면 드래프트도 새 값을 따라간다.
function PathPickSearchProductsProductListFilter({ isOpen, onClose }: Props) {
  const { filter, apply, clearAll, removeValue } = usePathPickProductFilter();
  const groups = usePathPickProductFilterGroups();
  // 범위 필터는 크림·포이즌 비교 값으로 거르는 조건이라 비교 표시를 끄면 함께 감춘다.
  const { isComparisonVisible } = usePathPickProductCardView();
  const { draft, setDraftValues, updateDraft, discard } =
    usePathPickFilterDraft<PathPickProductFilter>(filter);

  // 패널이 닫히면(취소·상세 필터 버튼) 적용하지 않은 수정은 버린다.
  useEffect(() => {
    if (!isOpen) {
      discard();
    }
  }, [isOpen, discard]);

  const getSelectedValues = (key: PathPickProductFilterKey) =>
    getPathPickProductFilterValues(draft, key);

  const getAppliedValues = (key: PathPickProductFilterKey) =>
    getPathPickProductFilterValues(filter, key);

  // 드래프트를 클로저로 읽지 않고 updateDraft로 최신 값을 받아 참조를 고정한다.
  const handleToggle = useCallback(
    (key: PathPickProductFilterKey, value: string | number) => {
      updateDraft((current) =>
        togglePathPickProductFilterValue(current, key, value)
      );
    },
    [updateDraft]
  );

  // 범위 칩 삭제는 다른 칩처럼 즉시 반영한다.
  const handleRemoveRange = useCallback(
    (patch: PathPickRangeFilter) => {
      apply({ ...filter, ...patch });
    },
    [filter, apply]
  );
  const rangeChips = usePathPickRangeFilterChips(filter, handleRemoveRange);

  // 적용 후에도 패널은 열어 둔다 — 적용값이 드래프트와 같아지므로 덮어쓴 값만 비운다.
  const handleApply = () => {
    apply(draft);
    discard();
  };

  return (
    <PathPickFilterPanel
      isOpen={isOpen}
      groups={groups}
      getSelectedValues={getSelectedValues}
      getAppliedValues={getAppliedValues}
      onToggle={handleToggle}
      onRemoveChip={removeValue}
      onClearAll={clearAll}
      onApply={handleApply}
      onCancel={onClose}
      isApplyDisabled={getInvalidPathPickRangeFields(draft).length > 0}
      extraCells={
        isComparisonVisible ? (
          <PathPickRangeFilterSection value={draft} onChange={setDraftValues} />
        ) : undefined
      }
      extraChips={rangeChips}
    />
  );
}

// 브랜드 옵션(filter-info 전체 브랜드)을 칩이 있으면 숨긴 채로도 전부 그려 수천 노드가 된다.
// 헤더는 선택이 바뀔 때마다 리렌더되므로 memo로 끊는다 — prop은 isOpen·onClose(고정 참조)뿐이고
// 나머지는 자체 훅(쿼리스트링·filter-info)에서 읽어, 선택 변경에는 다시 그릴 이유가 없다.
export default React.memo(PathPickSearchProductsProductListFilter);
